import React, { useState } from 'react';
import TabelaCrud from '../components/TabelaCrud';
import api, { getCurrentTenantId } from '../services/api';
import { useCreches } from '../hooks/useCreches';
import { useCriancas } from '../hooks/useCriancas';
import { formatCpf, isValidCpf, isValidEmail, isValidPhone } from '../common/validators';

const MAX_DOC_MB = 3;

interface ResponsavelLegal {
  id: number;
  creche_id: number;
  aluno_id: number;
  nome: string;
  cpf: string;
  rg: string;
  endereco: string;
  telefone: string;
  whatsapp: string;
  email: string;
  parentesco: string;
  profissao: string;
  nacionalidade: string;
  estado_civil: string;
  responsavel_financeiro: boolean;
}

interface ContatoEmergencia {
  id: number;
  creche_id: number;
  aluno_id: number;
  nome: string;
  telefone: string;
  whatsapp: string;
  email: string;
  relacao: string;
}

interface AutorizacaoFamilia {
  id: number;
  creche_id: number;
  aluno_id: number;
  responsavel_id: number;
  pode_retirar: boolean;
  admin_medicamentos: boolean;
  consentimento_fotos: boolean;
  consentimento_atividades_externas: boolean;
  documento_consentimento: string;
}

interface AgendaFamiliar {
  id: number;
  creche_id: number;
  aluno_id: number;
  tipo: string;
  titulo: string;
  data: string;
  descricao: string;
}

export default function FamiliasPage() {
  const { options } = useCreches();
  const { criancas } = useCriancas();
  const [respDaMatricula, setRespDaMatricula] = useState<{ aluno_id: number; nome: string } | null>(null);

  /** Carrega o nome do responsável legal da criança direto da matrícula (GET /alunos/:id). */
  const carregarRespMatricula = async (alunoId: number) => {
    if (!alunoId) { setRespDaMatricula(null); return; }
    try {
      const { data: a } = await api.get<any>(`/alunos/${alunoId}`);
      setRespDaMatricula({ aluno_id: alunoId, nome: a.responsavel_nome || '' });
    } catch { setRespDaMatricula(null); }
  };

  // Gestor/professor entram já vinculados a UMA creche (id_creche no token) — o
  // tenant vem do login, não se seleciona creche. Só super_admin sem creche
  // ativa precisa escolher. Ver [[bug-usuarios-page-creche-id]].
  const tenantScoped = Boolean(getCurrentTenantId());
  const criancaOptions = criancas.map((c) => ({ value: c.id, label: c.nome }));
  const nomeCriancaById = (id: number) => criancas.find((c) => c.id === id)?.nome || (id ? `#${id}` : '—');

  /**
   * Ao escolher a criança, puxa AUTOMATICAMENTE os dados do responsável legal já
   * informados na matrícula (cadastro da criança) — evita redigitar. O usuário só
   * complementa (ex.: marcar "responsável financeiro").
   */
  const autoPreencherResponsavel = async (alunoId: number, onChange: (c: any, v: any) => void) => {
    onChange('aluno_id', alunoId);
    if (!alunoId) return;
    try {
      const { data: a } = await api.get<any>(`/alunos/${alunoId}`);
      onChange('nome', a.responsavel_nome || '');
      onChange('parentesco', a.responsavel_parentesco || '');
      onChange('cpf', a.responsavel_cpf ? formatCpf(a.responsavel_cpf) : '');
      onChange('rg', a.responsavel_rg || '');
      onChange('endereco', a.responsavel_endereco || '');
      onChange('telefone', a.responsavel_telefone || '');
      onChange('whatsapp', a.responsavel_telefone || '');
      onChange('email', a.responsavel_email || '');
      onChange('profissao', a.responsavel_profissao || '');
      onChange('nacionalidade', a.responsavel_nacionalidade || '');
      onChange('estado_civil', a.responsavel_estado_civil || '');
    } catch { /* silencioso — mantém edição manual */ }
  };

  /** Lê um arquivo como data URI para embutir no documento de consentimento. */
  const lerArquivoComoDataUri = (file: File, onChange: (c: any, v: any) => void) => {
    if (file.size > MAX_DOC_MB * 1024 * 1024) {
      alert(`Arquivo muito grande. Máximo ${MAX_DOC_MB} MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange('documento_consentimento', String(reader.result || ''));
    reader.readAsDataURL(file);
  };

  return (
    <div className="stack-cadastros">
      <TabelaCrud<ResponsavelLegal>
        titulo="Famílias — Responsáveis Legais"
        endpoint="/familias/responsaveis"
        colunas={[
          { chave: 'id', label: 'ID' },
          { chave: 'nome', label: 'Nome' },
          { chave: 'parentesco', label: 'Parentesco' },
          { chave: 'email', label: 'E-mail' },
        ]}
        itemVazio={{ creche_id: 0, aluno_id: 0, nome: '', cpf: '', rg: '', endereco: '', telefone: '', whatsapp: '', email: '', parentesco: '', profissao: '', nacionalidade: '', estado_civil: '', responsavel_financeiro: false }}
        recarregarAoEditar
        validateItem={(item) => {
          if (!tenantScoped && !item.creche_id) return 'Selecione a creche.';
          if (!item.aluno_id) return 'Selecione a criança.';
          if (!item.nome?.trim()) return 'Nome do responsável é obrigatório.';
          // Dados vêm da matrícula (auto-preenchidos): valida só o que estiver preenchido.
          if (item.cpf && !isValidCpf(item.cpf)) return 'CPF inválido.';
          if (item.telefone && !isValidPhone(item.telefone, 'mobile')) return 'Telefone inválido.';
          if (item.whatsapp && !isValidPhone(item.whatsapp, 'mobile')) return 'WhatsApp inválido.';
          if (item.email && !isValidEmail(item.email)) return 'E-mail inválido.';
          return null;
        }}
        renderFormulario={(item, onChange) => (
          <>
            <div className="linha-campos linha-2">
              <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche
                <select value={item.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}>
                  <option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <label>Criança
                <select value={item.aluno_id ?? 0} onChange={(e) => autoPreencherResponsavel(Number(e.target.value), onChange)}>
                  <option value={0}>Selecione...</option>{criancaOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
            </div>
            <p style={{ fontSize: 12, color: '#2e7d32', margin: '2px 0 8px' }}>
              ✓ Ao escolher a criança, os dados do responsável legal são preenchidos automaticamente a partir da matrícula. Complemente/ajuste se necessário.
            </p>
            <div className="linha-campos linha-2">
              <label>Nome<input value={item.nome ?? ''} onChange={(e) => onChange('nome', e.target.value)} /></label>
              <label>Parentesco<input value={item.parentesco ?? ''} onChange={(e) => onChange('parentesco', e.target.value)} /></label>
            </div>
            <div className="linha-campos linha-2">
              <label>CPF<input value={item.cpf ?? ''} onChange={(e) => onChange('cpf', formatCpf(e.target.value))} /></label>
              <label>RG<input value={item.rg ?? ''} onChange={(e) => onChange('rg', e.target.value)} /></label>
            </div>
            {/* Qualificação civil — o contrato de matrícula exige estes três. */}
            <div className="linha-campos linha-3">
              <label>Profissão<input value={item.profissao ?? ''} onChange={(e) => onChange('profissao', e.target.value)} placeholder="Ex: Administrador(a)" /></label>
              <label>Nacionalidade<input value={item.nacionalidade ?? ''} onChange={(e) => onChange('nacionalidade', e.target.value)} placeholder="Brasileira" /></label>
              <label>Estado civil
                <select value={item.estado_civil ?? ''} onChange={(e) => onChange('estado_civil', e.target.value)}>
                  <option value="">Selecione...</option>
                  <option value="solteiro(a)">Solteiro(a)</option>
                  <option value="casado(a)">Casado(a)</option>
                  <option value="divorciado(a)">Divorciado(a)</option>
                  <option value="viúvo(a)">Viúvo(a)</option>
                  <option value="união estável">União estável</option>
                </select>
              </label>
            </div>
            <label>Endereço<input value={item.endereco ?? ''} onChange={(e) => onChange('endereco', e.target.value)} /></label>
            <div className="linha-campos linha-3">
              <label>Telefone<input value={item.telefone ?? ''} onChange={(e) => onChange('telefone', e.target.value)} /></label>
              <label>WhatsApp<input value={item.whatsapp ?? ''} onChange={(e) => onChange('whatsapp', e.target.value)} /></label>
              <label>E-mail<input type="email" value={item.email ?? ''} onChange={(e) => onChange('email', e.target.value)} /></label>
            </div>
            <label><input type="checkbox" checked={item.responsavel_financeiro ?? false} onChange={(e) => onChange('responsavel_financeiro', e.target.checked)} /> Responsável financeiro</label>
          </>
        )}
      />

      <TabelaCrud<ContatoEmergencia>
        titulo="Famílias — Contatos de Emergência"
        endpoint="/familias/contatos-emergencia"
        colunas={[{ chave: 'id', label: 'ID' }, { chave: 'nome', label: 'Nome' }, { chave: 'relacao', label: 'Relação' }, { chave: 'telefone', label: 'Telefone' }]}
        itemVazio={{ creche_id: 0, aluno_id: 0, nome: '', telefone: '', whatsapp: '', email: '', relacao: '' }}
        validateItem={(item) => {
          if (!tenantScoped && !item.creche_id) return 'Selecione a creche.';
          if (!item.aluno_id) return 'Selecione a criança.';
          if (!item.nome?.trim()) return 'Nome obrigatório.';
          if (!isValidPhone(item.telefone || '', 'mobile')) return 'Telefone inválido.';
          if (!isValidPhone(item.whatsapp || '', 'mobile')) return 'WhatsApp inválido.';
          if (!isValidEmail(item.email || '')) return 'E-mail inválido.';
          return null;
        }}
        renderFormulario={(item, onChange) => (
          <>
            <div className="linha-campos linha-2">
              <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche<select value={item.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label>Criança<select value={item.aluno_id ?? 0} onChange={(e) => onChange('aluno_id', Number(e.target.value))}><option value={0}>Selecione...</option>{criancaOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
            </div>
            <div className="linha-campos linha-2">
              <label>Nome<input value={item.nome ?? ''} onChange={(e) => onChange('nome', e.target.value)} /></label>
              <label>Relação<input value={item.relacao ?? ''} onChange={(e) => onChange('relacao', e.target.value)} /></label>
            </div>
            <div className="linha-campos linha-3">
              <label>Telefone<input value={item.telefone ?? ''} onChange={(e) => onChange('telefone', e.target.value)} /></label>
              <label>WhatsApp<input value={item.whatsapp ?? ''} onChange={(e) => onChange('whatsapp', e.target.value)} /></label>
              <label>E-mail<input value={item.email ?? ''} onChange={(e) => onChange('email', e.target.value)} /></label>
            </div>
          </>
        )}
      />

      <TabelaCrud<AutorizacaoFamilia>
        titulo="Famílias — Autorizações e Consentimentos"
        endpoint="/familias/autorizacoes"
        recarregarAoEditar
        colunas={[
          { chave: 'id', label: 'ID' },
          { chave: 'aluno_id', label: 'Criança', render: (r) => nomeCriancaById(r.aluno_id) },
          { chave: 'pode_retirar', label: 'Autorizações', render: (r) => [
            r.pode_retirar && 'Retirar', r.admin_medicamentos && 'Medicamentos', r.consentimento_fotos && 'Fotos', r.consentimento_atividades_externas && 'Atividades',
          ].filter(Boolean).join(' · ') || '—' },
        ]}
        itemVazio={{ creche_id: 0, aluno_id: 0, responsavel_id: 0, pode_retirar: false, admin_medicamentos: false, consentimento_fotos: false, consentimento_atividades_externas: false, documento_consentimento: '' }}
        validateItem={(item) => {
          if (!tenantScoped && !item.creche_id) return 'Selecione a creche.';
          if (!item.aluno_id) return 'Selecione a criança.';
          return null;
        }}
        renderFormulario={(item, onChange) => {
          const doc = item.documento_consentimento || '';
          const docEhArquivo = /^data:/.test(doc);
          const nomeResp = respDaMatricula && respDaMatricula.aluno_id === item.aluno_id ? respDaMatricula.nome : '';
          return (
            <>
              <div className="linha-campos linha-2">
                <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche<select value={item.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
                <label>Criança
                  <select value={item.aluno_id ?? 0} onChange={(e) => {
                    const alunoId = Number(e.target.value);
                    onChange('aluno_id', alunoId);
                    onChange('responsavel_id', 0); // o backend vincula o responsável da matrícula
                    carregarRespMatricula(alunoId);
                  }}>
                    <option value={0}>Selecione...</option>{criancaOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
              </div>

              {item.aluno_id ? (
                <p style={{ fontSize: 13, color: '#2e7d32', margin: '2px 0 8px' }}>
                  ✓ Responsável legal {nomeResp ? <>— <strong>{nomeResp}</strong> — </> : ''}vinculado automaticamente pela matrícula. Só o responsável legal pode autorizar.
                </p>
              ) : null}

              <label>Documento de consentimento (arquivo — PDF/imagem — ou URL)
                <input type="file" accept="image/*,application/pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) lerArquivoComoDataUri(f, onChange); }} />
              </label>
              {doc ? (
                <p style={{ fontSize: 13, margin: '4px 0' }}>
                  📎 <a href={doc} target="_blank" rel="noreferrer" download={docEhArquivo ? 'consentimento' : undefined}>Ver documento anexado</a>
                  {' '}<button type="button" onClick={() => onChange('documento_consentimento', '')} style={{ border: 'none', background: 'transparent', color: '#c62828', cursor: 'pointer' }}>remover</button>
                </p>
              ) : null}
              <label>ou cole uma URL
                <input value={docEhArquivo ? '' : doc} placeholder="https://…" onChange={(e) => onChange('documento_consentimento', e.target.value)} />
              </label>

              <label><input type="checkbox" checked={item.pode_retirar ?? false} onChange={(e) => onChange('pode_retirar', e.target.checked)} /> Pode retirar a criança</label>
              <label><input type="checkbox" checked={item.admin_medicamentos ?? false} onChange={(e) => onChange('admin_medicamentos', e.target.checked)} /> Autoriza administração de medicamentos</label>
              <label><input type="checkbox" checked={item.consentimento_fotos ?? false} onChange={(e) => onChange('consentimento_fotos', e.target.checked)} /> Consentimento para fotos</label>
              <label><input type="checkbox" checked={item.consentimento_atividades_externas ?? false} onChange={(e) => onChange('consentimento_atividades_externas', e.target.checked)} /> Consentimento para atividades externas</label>
            </>
          );
        }}
      />

      <TabelaCrud<AgendaFamiliar>
        titulo="Famílias — Agenda Familiar"
        endpoint="/familias/agenda"
        colunas={[{ chave: 'id', label: 'ID' }, { chave: 'tipo', label: 'Tipo' }, { chave: 'titulo', label: 'Título' }, { chave: 'data', label: 'Data' }]}
        itemVazio={{ creche_id: 0, aluno_id: 0, tipo: 'reuniao', titulo: '', data: '', descricao: '' }}
        validateItem={(item) => {
          if (!tenantScoped && !item.creche_id) return 'Selecione a creche.';
          if (!item.aluno_id || !item.titulo || !item.data) return 'Preencha criança, título e data.';
          return null;
        }}
        renderFormulario={(item, onChange) => (
          <>
            <div className="linha-campos linha-2">
              <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche<select value={item.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label>Criança<select value={item.aluno_id ?? 0} onChange={(e) => onChange('aluno_id', Number(e.target.value))}><option value={0}>Selecione...</option>{criancaOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
            </div>
            <div className="linha-campos linha-3">
              <label>Tipo<select value={item.tipo ?? 'reuniao'} onChange={(e) => onChange('tipo', e.target.value)}><option value="reuniao">Reunião</option><option value="visita">Visita</option><option value="evento">Evento escolar</option></select></label>
              <label>Título<input value={item.titulo ?? ''} onChange={(e) => onChange('titulo', e.target.value)} /></label>
              <label>Data<input type="date" value={item.data ?? ''} onChange={(e) => onChange('data', e.target.value)} /></label>
            </div>
            <label>Descrição<textarea rows={2} value={item.descricao ?? ''} onChange={(e) => onChange('descricao', e.target.value)} /></label>
          </>
        )}
      />
    </div>
  );
}

